import { Color } from "cesium";

/**
 * Solo mode — debriefing one pilot without losing the group.
 *
 * Solo is not a fourth independent switch: it is **camera + Vario Track + every
 * other track dimmed**, all on one pilot. The toolbar sets the first two and
 * this module does the dimming, and `showHideFile` keeps the three together
 * when the soloed flight is unchecked. Only offered with 2+ flights loaded —
 * with one there is nothing to dim and nothing to focus away from.
 *
 * The others stay on screen on purpose: in a debriefing the question is usually
 * *where was everyone else when this happened*, so removing them would defeat
 * the point. They are faded, not hidden.
 *
 * ## What gets dimmed
 *
 * A plain (non-coloured) flight is one CZML entity per file with three visible
 * parts, and they need different treatment:
 *  - `path` — the trailing track. Alpha alone is NOT enough here, measured on a
 *    10-pilot alpine flight: a magenta line at 30% over green terrain still won
 *    the eye, because hue contrast survives blending and a 3 px line stays 3 px
 *    wide however transparent it is. So it loses chroma (`DIM_DESATURATE`),
 *    alpha (`DIM_ALPHA`) and width (`DIM_WIDTH`) together. The material's colour
 *    AND `outlineColor` both fade; doing only one leaves a 5 px outline at full
 *    strength around a faded 3 px line, which reads *louder*, not quieter.
 *  - `billboard` — the pilot-name marker (an SVG data URI). Tinted down through
 *    `billboard.color`, which multiplies the texture, so no SVG is rebuilt.
 *  - `label` — the altitude readout. Switched **off** rather than faded: a faint
 *    two-colour outlined text is an illegible smudge, and ten of them are the
 *    clutter you were trying to get away from. The pilot's *name* is in the
 *    billboard, so the others stay identifiable.
 *
 * The soloed pilot is the coloured flight, so its track is the vario primitive
 * (`varioTrack.js`) and never passes through here.
 *
 * ## Why the original properties are kept, not recomputed
 *
 * Restoring means putting the exact `Property` objects back, so `_capture`
 * stores them on the entity the first time it is touched. Recomputing "full
 * strength" from the flight's colour instead would quietly hard-code the
 * assumption that tracks are opaque and that nothing else ever sets these.
 * Entities are rebuilt whenever the Vario Track switches, and a fresh entity
 * simply has no capture yet — which is exactly right.
 */

/**
 * How a dimmed track is drawn. Purely perceptual, and it has to hold over both
 * bright snow and dark forest in the same frame, so it is tunable live from the
 * console — `viewer.__solo.tune({ alpha: 0.4 })` repaints immediately. Change
 * the defaults here once a combination proves itself.
 *
 * Chroma is the lever that matters for *not competing*: a magenta line at 30%
 * alpha still won the eye over green terrain, because hue contrast survives
 * blending. So the chroma goes first, and alpha and width stay low.
 *
 * `outlineWidth` interacts with `width` in a way that is easy to get wrong. The
 * shader computes `halfInteriorWidth = 0.5 * (width - outlineWidth) / width`, so
 * an `outlineWidth` at or above `width` makes the WHOLE line render in
 * `outlineColor` and `color` never shows — which is the case for the app's
 * full-strength tracks (width 3, outlineWidth 5). At 0 the line is all `color`,
 * with `outlineColor` left tinting only the antialiased rim. Raise it above 0
 * only together with a `width` that leaves room for an interior.
 */
const dim = {
  alpha: 0.32, // of the line itself, after desaturation
  desaturate: 0.78, // how far the colour is pulled to its own grey
  width: 1, // px total, down from the full-strength 3
  outlineWidth: 0, // px of dark edge INSIDE that width — see the note below
  outlineAlpha: 0.45, // of that edge; also tints the antialiased rim at width 0
};

let soloId = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(soloId));

export const getSoloFlight = () => soloId;

export const onSoloChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Solo `id` (null = off) and repaint everything on screen. */
export function setSoloFlight(viewer, id) {
  soloId = id || null;
  applySolo(viewer);
  notify();
}

/** Adjust the dim look and repaint. Returns the settings in force. */
export function tuneSolo(viewer, patch) {
  Object.assign(dim, patch);
  applySolo(viewer);
  return { ...dim };
}

/** Drop the solo flight if it is no longer loaded. */
export function pruneSolo(viewer, ids) {
  if (soloId && !ids.includes(soloId)) setSoloFlight(viewer, null);
}

/**
 * Re-apply the current solo state to every entity on screen. Cheap — one
 * entity per flight — so callers just run it after any data source is built or
 * swapped rather than tracking which one changed.
 */
export function applySolo(viewer) {
  viewer.__solo = { tune: (patch) => tuneSolo(viewer, patch), dim };
  const time = viewer.clock.currentTime;
  const dataSources = viewer.dataSources;
  for (let i = 0; i < dataSources.length; i++) {
    const entities = dataSources.get(i).entities.values;
    for (let j = 0; j < entities.length; j++) {
      const entity = entities[j];
      _apply(entity, soloId != null && entity.id !== soloId, time);
    }
  }
}

function _apply(entity, faded, time) {
  const base = _capture(entity);

  const path = entity.path;
  const material = path && path.material;
  if (material) {
    material.color = faded ? _fade(base.pathColor, time) : base.pathColor;
    // A dark edge, not the track's own colour: contrast against the ground is
    // what keeps a low-chroma line readable over snow AND forest, and it costs
    // no attention because it has no hue of its own.
    material.outlineColor = faded
      ? Color.BLACK.withAlpha(dim.outlineAlpha)
      : base.pathOutline;
    material.outlineWidth = faded ? dim.outlineWidth : base.pathOutlineWidth;
  }
  if (path) {
    path.width = faded ? dim.width : base.pathWidth;
  }
  if (entity.billboard) {
    // undefined restores Cesium's default (no tint) when nothing was set.
    entity.billboard.color = faded ? Color.WHITE.withAlpha(dim.alpha) : base.billboardColor;
  }
  if (entity.label) {
    entity.label.show = faded ? false : base.labelShow;
  }
}

/** The entity's untouched properties, captured once. */
function _capture(entity) {
  if (entity.__soloBase) return entity.__soloBase;
  const base = {};
  if (entity.path) {
    base.pathWidth = entity.path.width;
    const material = entity.path.material;
    if (material) {
      base.pathColor = material.color;
      base.pathOutline = material.outlineColor;
      base.pathOutlineWidth = material.outlineWidth;
    }
  }
  if (entity.billboard) base.billboardColor = entity.billboard.color;
  if (entity.label) base.labelShow = entity.label.show;
  entity.__soloBase = base;
  return base;
}

/**
 * The same fade as a CSS colour, for the altitude chart — so the globe and the
 * chart agree on what "dimmed" means from one definition. Takes the flight's
 * `[r, g, b]` (0-255) as `igcParsedData` carries it.
 */
export function dimmedCss(rgb) {
  const [r, g, b] = rgb;
  const grey = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const mix = (v) => Math.round(v + (grey - v) * dim.desaturate);
  return `rgba(${mix(r)},${mix(g)},${mix(b)},${dim.alpha})`;
}

/** Original colour → low-chroma, low-alpha version of itself. */
function _fade(property, time) {
  const color = property && property.getValue ? property.getValue(time) : undefined;
  if (!color) return undefined;
  // Pull towards the colour's own luminance, so every track fades to the same
  // neutral rather than towards one shared grey — the tracks stay tellable
  // apart by brightness while none of them shouts.
  const grey = color.red * 0.2126 + color.green * 0.7152 + color.blue * 0.0722;
  const mix = (v) => v + (grey - v) * dim.desaturate;
  return new Color(mix(color.red), mix(color.green), mix(color.blue), color.alpha * dim.alpha);
}
